import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, TrendingUp, CheckCircle, XCircle, Eye, Search, Tag } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { formatDistanceToNow } from '../utils/date-utils';
import { ConfirmDialog } from './ConfirmDialog';
import { useAdminModalZIndex } from './AdminModalZContext';
import { devError } from '../utils/logger';


interface DiscountCode {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: 'percentage' | 'fixed_amount' | 'lifetime_free';
  value?: number;
  applicablePlans: string[];
  duration: 'once' | 'repeating' | 'forever';
  durationInMonths?: number;
  maxUses?: number;
  timesUsed: number;
  maxUsesPerUser: number;
  validFrom: string;
  validUntil?: string;
  active: boolean;
  stripeCouponId?: string;
  createdAt: string;
}

interface CreateDiscountInput {
  code: string;
  name: string;
  description?: string;
  type: 'percentage' | 'fixed_amount' | 'lifetime_free';
  value?: number;
  applicable_plans: string[];
  duration: 'once' | 'repeating' | 'forever';
  duration_in_months?: number;
  max_uses?: number;
  max_uses_per_user?: number;
  valid_from?: string;
  valid_until?: string;
}

export function AdminDiscountCodes({ accessToken }: { accessToken: string }) {
  const adminModalZ = useAdminModalZIndex();
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCode, setEditingCode] = useState<DiscountCode | null>(null);
  
  // Confirm dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState<string | null>(null);
  const [deactivatingCode, setDeactivatingCode] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<CreateDiscountInput>({
    code: '',
    name: '',
    description: '',
    type: 'percentage',
    value: undefined,
    applicable_plans: ['all'],
    duration: 'once',
    duration_in_months: undefined,
    max_uses: undefined,
    max_uses_per_user: 1,
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: undefined,
  });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/admin/discount-codes`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCodes(data.codes);
      } else {
        toast.error('Kunne ikke laste rabattkoder');
      }
    } catch (error) {
      devError('Error fetching discount codes:', error);
      toast.error('Feil ved lasting av rabattkoder');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch(
        `${API_BASE}/admin/discount-codes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        toast.success('Rabattkode opprettet!');
        setShowCreateModal(false);
        resetForm();
        fetchCodes();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Kunne ikke opprette rabattkode');
      }
    } catch (error) {
      devError('Error creating discount code:', error);
      toast.error('Feil ved opprettelse av rabattkode');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingCode) return;

    try {
      const response = await fetch(
        `${API_BASE}/admin/discount-codes/${editingCode.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        toast.success('Rabattkode oppdatert!');
        setEditingCode(null);
        resetForm();
        fetchCodes();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Kunne ikke oppdatere rabattkode');
      }
    } catch (error) {
      devError('Error updating discount code:', error);
      toast.error('Feil ved oppdatering av rabattkode');
    }
  };

  const handleDelete = async () => {
    if (!codeToDelete) return;
    
    setDeactivatingCode(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/discount-codes/${codeToDelete}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Rabattkode deaktivert!');
        fetchCodes();
      } else {
        toast.error('Kunne ikke deaktivere rabattkode');
      }
    } catch (error) {
      devError('Error deleting discount code:', error);
      toast.error('Feil ved deaktivering av rabattkode');
    } finally {
      setDeactivatingCode(false);
      setShowDeleteConfirm(false);
      setCodeToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      type: 'percentage',
      value: undefined,
      applicable_plans: ['all'],
      duration: 'once',
      duration_in_months: undefined,
      max_uses: undefined,
      max_uses_per_user: 1,
      valid_from: new Date().toISOString().split('T')[0],
      valid_until: undefined,
    });
  };

  const openEditModal = (code: DiscountCode) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      name: code.name,
      description: code.description,
      type: code.type,
      value: code.value,
      applicable_plans: code.applicablePlans,
      duration: code.duration,
      duration_in_months: code.durationInMonths,
      max_uses: code.maxUses,
      max_uses_per_user: code.maxUsesPerUser,
      valid_from: code.validFrom?.split('T')[0],
      valid_until: code.validUntil?.split('T')[0],
    });
    setShowCreateModal(true);
  };

  const filteredCodes = codes.filter(code => 
    code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeLabel = (type: string, value?: number) => {
    switch (type) {
      case 'percentage':
        return `${value}% rabatt`;
      case 'fixed_amount':
        return `${value} kr rabatt`;
      case 'lifetime_free':
        return 'Gratis for livet';
      default:
        return type;
    }
  };

  const getStatusColor = (code: DiscountCode) => {
    if (!code.active) return 'bg-gray-100 text-gray-700';
    
    const now = new Date();
    const validUntil = code.validUntil ? new Date(code.validUntil) : null;
    
    if (validUntil && now > validUntil) return 'bg-red-100 text-red-700';
    if (code.maxUses && code.timesUsed >= code.maxUses) return 'bg-orange-100 text-orange-700';
    return 'bg-green-100 text-green-700';
  };

  const getStatusLabel = (code: DiscountCode) => {
    if (!code.active) return 'Deaktivert';
    
    const now = new Date();
    const validUntil = code.validUntil ? new Date(code.validUntil) : null;
    
    if (validUntil && now > validUntil) return 'Utløpt';
    if (code.maxUses && code.timesUsed >= code.maxUses) return 'Maks bruk nådd';
    return 'Aktiv';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader size="lg" className="text-purple-600" />
        <p className="mt-4 text-gray-600">Laster rabattkoder...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl text-gray-800 flex items-center gap-2">
            <Tag className="w-6 h-6 text-purple-600" />
            Rabattkoder
          </h2>
          <p className="text-gray-600 mt-1">Administrer discount codes og kampanjer</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingCode(null);
            setShowCreateModal(true);
          }}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Opprett kode
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-purple-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Søk etter kode eller navn..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      {/* Codes List */}
      <div className="bg-white rounded-2xl shadow-lg border border-purple-100 overflow-hidden">
        {filteredCodes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {searchTerm ? 'Ingen rabattkoder funnet' : 'Ingen rabattkoder opprettet ennå'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-pink-50">
                <tr>
                  <th className="px-6 py-4 text-left text-gray-700">Kode</th>
                  <th className="px-6 py-4 text-left text-gray-700">Navn</th>
                  <th className="px-6 py-4 text-left text-gray-700">Type</th>
                  <th className="px-6 py-4 text-left text-gray-700">Planer</th>
                  <th className="px-6 py-4 text-left text-gray-700">Bruk</th>
                  <th className="px-6 py-4 text-left text-gray-700">Status</th>
                  <th className="px-6 py-4 text-left text-gray-700">Opprettet</th>
                  <th className="px-6 py-4 text-left text-gray-700">Handlinger</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCodes.map((code) => (
                  <tr key={code.id} className="hover:bg-purple-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-mono font-bold text-purple-900">{code.code}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-900">{code.name}</span>
                      {code.description && (
                        <p className="text-sm text-gray-500 mt-1">{code.description}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-800">{getTypeLabel(code.type, code.value)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {code.applicablePlans.includes('all') ? 'Alle' : code.applicablePlans.join(', ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900">{code.timesUsed}</span>
                        <span className="text-gray-400">/</span>
                        <span className="text-gray-600">{code.maxUses || '∞'}</span>
                      </div>
                      {code.maxUses && (
                        <div className="mt-1 w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-purple-600 to-pink-600"
                            style={{ width: `${Math.min((code.timesUsed / code.maxUses) * 100, 100)}%` }}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(code)}`}>
                        {getStatusLabel(code)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {formatDistanceToNow(code.createdAt)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(code)}
                          className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
                          title="Rediger"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setCodeToDelete(code.id);
                            setShowDeleteConfirm(true);
                          }}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                          title="Deaktiver"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-2xl text-gray-800 mb-6">
              {editingCode ? 'Rediger rabattkode' : 'Opprett ny rabattkode'}
            </h3>

            <form onSubmit={editingCode ? handleUpdate : handleCreate} className="space-y-4">
              {/* Code and Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-2">
                    Kode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                    placeholder="SOMMER2024"
                    required
                    disabled={!!editingCode}
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">
                    Navn <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Sommer 2024 kampanje"
                    required
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-gray-700 mb-2">Beskrivelse</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={2}
                  placeholder="Intern beskrivelse..."
                />
              </div>

              {/* Type and Value */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-2">
                    Rabatttype <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="percentage">Prosent (%)</option>
                    <option value="fixed_amount">Fast beløp (kr)</option>
                    <option value="lifetime_free">Gratis for livet</option>
                  </select>
                </div>
                {formData.type !== 'lifetime_free' && (
                  <div>
                    <label className="block text-gray-700 mb-2">
                      Verdi <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.value || ''}
                      onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder={formData.type === 'percentage' ? '20' : '100'}
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                )}
              </div>

              {/* Applicable Plans */}
              <div>
                <label className="block text-gray-700 mb-2">
                  Gjelder for planer <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.applicable_plans.includes('all')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, applicable_plans: ['all'] });
                        } else {
                          setFormData({ ...formData, applicable_plans: [] });
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-gray-700">Alle planer</span>
                  </label>
                  {!formData.applicable_plans.includes('all') && (
                    <>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.applicable_plans.includes('talent_plus') || formData.applicable_plans.includes('talent_pluss')}
                          onChange={(e) => {
                            const plans = formData.applicable_plans.filter(p => p !== 'all');
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                applicable_plans: [...plans.filter(p => p !== 'talent_pluss' && p !== 'talent_plus'), 'talent_plus']
                              });
                            } else {
                              setFormData({
                                ...formData,
                                applicable_plans: plans.filter(p => p !== 'talent_pluss' && p !== 'talent_plus')
                              });
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-gray-700">Talentfunn Pluss</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.applicable_plans.includes('arbeidsgiver')}
                          onChange={(e) => {
                            const plans = formData.applicable_plans.filter(p => p !== 'all');
                            if (e.target.checked) {
                              setFormData({ ...formData, applicable_plans: [...plans, 'arbeidsgiver'] });
                            } else {
                              setFormData({ ...formData, applicable_plans: plans.filter(p => p !== 'arbeidsgiver') });
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-gray-700">Arbeidsgiver Basis</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.applicable_plans.includes('arbeidsgiver_plus') || formData.applicable_plans.includes('arbeidsgiver_pluss')}
                          onChange={(e) => {
                            const plans = formData.applicable_plans.filter(p => p !== 'all');
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                applicable_plans: [...plans.filter(p => p !== 'arbeidsgiver_plus' && p !== 'arbeidsgiver_pluss'), 'arbeidsgiver_plus']
                              });
                            } else {
                              setFormData({
                                ...formData,
                                applicable_plans: plans.filter(p => p !== 'arbeidsgiver_plus' && p !== 'arbeidsgiver_pluss')
                              });
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-gray-700">Arbeidsgiver Pluss</span>
                      </label>
                    </>
                  )}
                </div>
              </div>

              {/* Duration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-2">
                    Varighet <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="once">En gang</option>
                    <option value="repeating">Gjentakende</option>
                    <option value="forever">For alltid</option>
                  </select>
                </div>
                {formData.duration === 'repeating' && (
                  <div>
                    <label className="block text-gray-700 mb-2">
                      Antall måneder <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.duration_in_months || ''}
                      onChange={(e) => setFormData({ ...formData, duration_in_months: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="3"
                      required
                      min="1"
                    />
                  </div>
                )}
              </div>

              {/* Usage Limits */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-2">Maks antall bruk (totalt)</label>
                  <input
                    type="number"
                    value={formData.max_uses || ''}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Ubegrenset"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Maks bruk per bruker</label>
                  <input
                    type="number"
                    value={formData.max_uses_per_user}
                    onChange={(e) => setFormData({ ...formData, max_uses_per_user: parseInt(e.target.value) || 1 })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="1"
                    min="1"
                  />
                </div>
              </div>

              {/* Validity Period */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-2">Gyldig fra</label>
                  <input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Gyldig til</label>
                  <input
                    type="date"
                    value={formData.valid_until || ''}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value || undefined })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all"
                >
                  {editingCode ? 'Oppdater kode' : 'Opprett kode'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingCode(null);
                    resetForm();
                  }}
                  className="px-6 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-all"
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setCodeToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Deaktiver rabattkode?"
        message="Er du sikker på at du vil deaktivere denne rabattkoden?"
        confirmText="Deaktiver"
        cancelText="Avbryt"
        variant="warning"
        loading={deactivatingCode}
        loadingText="Deaktiverer..."
        portalZIndex={adminModalZ}
      />
    </div>
  );
}
